import { useMemo, useState } from 'react';
import {
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  LogIn,
  LogOut,
  ShieldCheck,
  XCircle,
} from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { formatClockTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { DayKind, StaffAttendanceDay } from '@/types/staffAttendance';
import { teacherApi } from '../../api/teacher.api';

/** How each kind of day is drawn, and what its tooltip says. */
const DAY_STYLE: Record<DayKind, { className: string; label: string; dotClass: string }> = {
  PRESENT: {
    className: 'border-emerald-500/30 bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
    dotClass: 'bg-emerald-500',
    label: 'Present — approved',
  },
  PENDING: {
    className: 'border-amber-500/30 bg-amber-500/15 text-amber-600 dark:text-amber-400',
    dotClass: 'bg-amber-500',
    label: 'Signed in — waiting for the office',
  },
  // Outlined and struck through rather than filled: otherwise a refused day and
  // an absent one are the same red square, and the legend cannot tell them apart.
  REJECTED: {
    className: 'border-rose-600 bg-surface text-rose-600 line-through dark:text-rose-400',
    dotClass: 'border border-rose-600 bg-surface',
    label: 'Punch-in refused',
  },
  ABSENT: {
    className: 'border-rose-500/30 bg-rose-500/10 text-rose-600 dark:text-rose-400',
    dotClass: 'bg-rose-500',
    label: 'No punch-in — absent',
  },
  LEAVE: {
    className: 'border-indigo-500/30 bg-indigo-500/15 text-indigo-600 dark:text-indigo-400',
    dotClass: 'bg-indigo-500',
    label: 'Approved leave',
  },
  WEEK_OFF: {
    className: 'border-line bg-surface-2 text-ink-3',
    dotClass: 'bg-surface-3 border border-line',
    label: 'Week off',
  },
  HOLIDAY: {
    className: 'border-blue-500/30 bg-blue-500/15 text-blue-600 dark:text-blue-400',
    dotClass: 'bg-blue-500',
    label: 'Institute holiday',
  },
  UPCOMING: {
    className: 'border-line bg-surface text-ink-3',
    dotClass: 'bg-surface',
    label: 'Still to come',
  },
};

const LEGEND: { kind: DayKind; displayLabel: string }[] = [
  { kind: 'PRESENT', displayLabel: 'Present' },
  { kind: 'PENDING', displayLabel: 'Signed in' },
  { kind: 'REJECTED', displayLabel: 'Refused' },
  { kind: 'ABSENT', displayLabel: 'No punch in' },
  { kind: 'LEAVE', displayLabel: 'Leave' },
  { kind: 'HOLIDAY', displayLabel: 'Institute holiday' },
  { kind: 'WEEK_OFF', displayLabel: 'Week off' },
];

const WEEKDAY_ORDER = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
];
const WEEKDAY_INITIALS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

function formatMonth(month: string) {
  return new Date(`${month}-01T00:00:00.000Z`).toLocaleDateString('en-IN', {
    timeZone: 'UTC',
    month: 'long',
    year: 'numeric',
  });
}

function shiftMonth(month: string, by: number) {
  const [year, monthNumber] = month.split('-').map(Number);
  const zeroBased = year * 12 + (monthNumber - 1) + by;

  return `${Math.floor(zeroBased / 12)}-${String((zeroBased % 12) + 1).padStart(2, '0')}`;
}

/** "8 hrs 01 min" — how long the day ran, once both ends are known. */
function formatWorked(minutes: number): string {
  return `${Math.floor(minutes / 60)} hrs ${String(minutes % 60).padStart(2, '0')} min`;
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString('en-IN', {
    timeZone: 'Asia/Kolkata',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function TeacherPunchInCard() {
  const [month, setMonth] = useState('');
  const [isPunching, setIsPunching] = useState(false);
  const [isPunchingOut, setIsPunchingOut] = useState(false);

  const { data, isLoading, error, refetch } = useApiQuery(
    () => teacherApi.myStaffAttendance(month || undefined),
    [month],
  );

  const grid = useMemo(() => {
    if (!data || data.days.length === 0) return [];

    const lead = WEEKDAY_ORDER.indexOf(data.days[0].weekday);
    return [...Array<StaffAttendanceDay | null>(Math.max(lead, 0)).fill(null), ...data.days];
  }, [data]);

  async function handlePunchIn() {
    setIsPunching(true);

    try {
      const punch = await teacherApi.punchIn();
      toast.success(
        `Signed in at ${formatTime(punch.punchedInAt)}`,
        'Your institute office will verify it.',
      );
      refetch();
    } catch (caught) {
      toast.error(
        'Could not sign you in',
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
      // The card was loaded before closing time and the school has closed
      // since. Reloaded, it shows the closed notice instead of the button.
      if (caught instanceof ApiRequestError && caught.code === 'SCHOOL_DAY_CLOSED') refetch();
    } finally {
      setIsPunching(false);
    }
  }

  /**
   * Recording the end of the day.
   *
   * Signing out is never required. A day with an arrival and no departure stays
   * a present day — people go home without pressing things — and it only leaves
   * the hours unknown, which is why nothing here nags about it.
   */
  async function handlePunchOut() {
    setIsPunchingOut(true);

    try {
      const punched = await teacherApi.punchOut();
      toast.success(
        `Signed out at ${formatTime(punched.punchedOutAt ?? punched.punchedInAt)}`,
        punched.workedMinutes === null
          ? 'Your day is recorded.'
          : `${formatWorked(punched.workedMinutes)} on institute today.`,
      );
      refetch();
    } catch (caught) {
      toast.error(
        'Could not sign you out',
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsPunchingOut(false);
    }
  }

  const punch = data?.today.punch ?? null;

  return (
    <div className="card bg-surface border border-line rounded-[16px] p-5 shadow-2xs flex flex-col gap-5">
      {/* --- HEADER ----------------------------------------------------------- */}
      <div className="flex items-center gap-3 border-b border-line pb-4">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px] bg-indigo-500/15 text-indigo-600 dark:text-indigo-400 border border-indigo-500/20">
          <ShieldCheck className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          <h2 className="text-base font-bold text-ink tracking-tight">Teacher Attendance</h2>
          <p className="text-xs text-ink-3 font-medium">Daily punch in · Your own attendance</p>
        </div>
      </div>

      {isLoading && <SkeletonLines count={9} />}

      {!isLoading && error && (
        <div className="flex flex-col items-start gap-3">
          <Alert tone="critical" title="Could not load your attendance">
            {error.message}
          </Alert>
          <Button variant="secondary" size="sm" onClick={refetch}>
            Try again
          </Button>
        </div>
      )}

      {!isLoading && !error && data && (
        <>
          {/* --- PUNCH IN BANNER ----------------------------------------------- */}
          {!punch && data.today.isWeekOff && (
            <div className="rounded-[14px] border border-line bg-surface-2 p-4">
              <p className="text-sm font-bold text-ink">Today is a week off</p>
              <p className="mt-0.5 text-xs text-ink-3 font-medium">
                Your institute does not work today, so there is nothing to sign.
              </p>
            </div>
          )}

          {!punch && !data.today.isWeekOff && data.today.isClosed && (
            <div className="rounded-[14px] border border-line bg-surface-2 p-4">
              <p className="text-sm font-bold text-ink">Sign-in has closed for today</p>
              <p className="mt-0.5 text-xs text-ink-3 font-medium">
                Your school closed at {formatClockTime(data.today.closesAt)}. If you were here today,
                please tell the school office.
              </p>
            </div>
          )}

          {!punch && !data.today.isWeekOff && !data.today.isClosed && (
            <div className="flex flex-col gap-3 rounded-[14px] border border-indigo-500/20 bg-indigo-500/5 dark:bg-indigo-500/10 p-4">
              <div className="flex items-start gap-3">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-indigo-500/20 text-indigo-600 dark:text-indigo-400 mt-0.5">
                  <LogIn className="h-4.5 w-4.5" />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-ink">Not signed in today</p>
                  <p className="text-xs text-ink-2 font-medium mt-0.5 leading-relaxed">
                    Punch in to record that you are on institute. The time is taken from the institute
                    clock, not your device.
                  </p>
                </div>
              </div>

              <Button
                variant="primary"
                size="sm"
                fullWidth
                leftIcon={<LogIn className="h-4 w-4" />}
                isLoading={isPunching}
                onClick={() => void handlePunchIn()}
                className="h-9 text-sm font-bold bg-indigo-600 hover:bg-indigo-700 text-white shadow-xs justify-center"
              >
                Punch in now
              </Button>
            </div>
          )}

          {punch && (
            <div
              className={cn(
                'flex flex-col gap-3 rounded-[14px] border p-4',
                punch.status === 'APPROVED' && 'border-emerald-500/30 bg-emerald-500/5',
                punch.status === 'PENDING' && 'border-amber-500/30 bg-amber-500/5',
                punch.status === 'REJECTED' && 'border-rose-500/30 bg-rose-500/5',
              )}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Badge
                  tone={
                    punch.status === 'APPROVED'
                      ? 'success'
                      : punch.status === 'PENDING'
                        ? 'warning'
                        : 'critical'
                  }
                >
                  {punch.status === 'APPROVED'
                    ? 'Approved'
                    : punch.status === 'PENDING'
                      ? 'Waiting for office'
                      : 'Refused'}
                </Badge>
                <span className="text-xs font-semibold text-ink-2">
                  Signed in at {formatTime(punch.punchedInAt)}
                  {punch.punchedOutAt &&
                    ` · out at ${formatTime(punch.punchedOutAt)}${punch.isAutoPunchOut ? ' (auto, school closed)' : ''}`}
                  {punch.workedMinutes !== null && ` · ${formatWorked(punch.workedMinutes)}`}
                </span>
              </div>

              <div className="flex items-center gap-3">
                <span
                  className={cn(
                    'grid h-9 w-9 shrink-0 place-items-center rounded-full',
                    punch.status === 'APPROVED' && 'bg-emerald-500/15 text-emerald-600',
                    punch.status === 'PENDING' && 'bg-amber-500/15 text-amber-600',
                    punch.status === 'REJECTED' && 'bg-rose-500/15 text-rose-600',
                  )}
                >
                  {punch.status === 'APPROVED' ? (
                    <CheckCircle2 className="h-4.5 w-4.5" />
                  ) : punch.status === 'PENDING' ? (
                    <Clock className="h-4.5 w-4.5" />
                  ) : (
                    <XCircle className="h-4.5 w-4.5" />
                  )}
                </span>

                <div className="min-w-0 text-xs">
                  {punch.status === 'PENDING' && (
                    <p className="text-ink-2 font-medium">
                      Your institute office checks punch-ins. Until then, counts as signed.
                    </p>
                  )}
                  {punch.status === 'APPROVED' && (
                    <p className="text-ink-2 font-medium">
                      Verified by {punch.reviewedBy ?? 'the office'}
                      {punch.reviewedAt && ` at ${formatTime(punch.reviewedAt)}`}.
                    </p>
                  )}
                  {punch.status === 'REJECTED' && (
                    <p className="text-ink-2 font-medium">
                      {punch.reviewedBy ?? 'The office'} did not accept this punch-in.
                    </p>
                  )}
                </div>
              </div>

              {data.today.canPunchOut && (
                <Button
                  variant="secondary"
                  size="sm"
                  fullWidth
                  leftIcon={<LogOut className="h-4 w-4" />}
                  isLoading={isPunchingOut}
                  onClick={() => void handlePunchOut()}
                  className="h-9 text-sm font-bold justify-center"
                >
                  Punch out
                </Button>
              )}
            </div>
          )}

          {/* --- THE CALENDAR WIDGET ------------------------------------------- */}
          <div className="flex flex-col gap-3.5 border-t border-line pt-4">
            <div className="flex items-center justify-between gap-2">
              <div className="flex min-w-0 items-center gap-2">
                <CalendarDays className="h-4 w-4 shrink-0 text-indigo-600 dark:text-indigo-400" />
                <h3 className="truncate text-sm font-bold text-ink">{formatMonth(data.month)}</h3>
              </div>

              <div className="flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  onClick={() => setMonth(shiftMonth(data.month, -1))}
                  className="grid h-7 w-7 place-items-center rounded-[6px] border border-line text-ink-2 hover:bg-surface-2 hover:text-ink transition-colors"
                  aria-label="Previous month"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  disabled={data.month >= data.today.date.slice(0, 7)}
                  onClick={() => setMonth(shiftMonth(data.month, 1))}
                  className="grid h-7 w-7 place-items-center rounded-[6px] border border-line text-ink-2 hover:bg-surface-2 hover:text-ink disabled:opacity-30 disabled:pointer-events-none transition-colors"
                  aria-label="Next month"
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>

            <div>
              {/* Day initials row */}
              <div className="grid grid-cols-7 gap-1 text-center text-2xs font-bold text-ink-3" aria-hidden>
                {WEEKDAY_INITIALS.map((initial, index) => (
                  <span key={WEEKDAY_ORDER[index]} className="py-1">{initial}</span>
                ))}
              </div>

              {/* Day boxes grid */}
              <div className="mt-1 grid grid-cols-7 gap-1">
                {grid.map((d, index) =>
                  d === null ? (
                    <span key={`blank-${index}`} aria-hidden />
                  ) : (
                    <span
                      key={d.date}
                      title={`${d.dayOfMonth} ${formatMonth(data.month)} — ${
                        d.holidayName ?? DAY_STYLE[d.kind].label
                      }`}
                      className={cn(
                        'grid h-8 place-items-center rounded-[8px] border text-xs font-bold transition-all',
                        DAY_STYLE[d.kind].className,
                        d.date === data.today.date &&
                          'ring-2 ring-indigo-600 dark:ring-indigo-400 ring-offset-1 ring-offset-surface font-black',
                      )}
                    >
                      {d.dayOfMonth}
                    </span>
                  ),
                )}
              </div>
            </div>

            {/* --- Month in Numbers stats bar -------------------------------- */}
            {/* Every kind of working day is counted, so the figures add up to the
                last one — leave and refused punch-ins included, and "not in yet"
                for today while it applies. */}
            <dl className="grid grid-cols-3 sm:grid-cols-4 gap-2 rounded-[12px] border border-line bg-surface-2 p-2.5 text-center">
              {[
                { label: 'Present', value: data.summary.present, tone: 'text-emerald-600 dark:text-emerald-400' },
                { label: 'Pending', value: data.summary.pending, tone: 'text-amber-500' },
                { label: 'Refused', value: data.summary.rejected, tone: 'text-rose-600 dark:text-rose-400' },
                { label: 'Absent', value: data.summary.absent, tone: 'text-rose-500' },
                { label: 'Leave', value: data.summary.leave, tone: 'text-indigo-600 dark:text-indigo-400' },
                ...(data.summary.notInYet > 0
                  ? [{ label: 'Not in yet', value: data.summary.notInYet, tone: 'text-ink-2' }]
                  : []),
                { label: 'Working days', value: data.summary.workingDaysSoFar, tone: 'text-ink' },
              ].map((stat) => (
                <div key={stat.label}>
                  <dt className="text-xs font-bold text-ink-3 uppercase tracking-wider">{stat.label}</dt>
                  <dd className={cn('mt-0.5 text-sm font-black', stat.tone)}>{stat.value}</dd>
                </div>
              ))}
            </dl>

            {/* --- Legend dots list ------------------------------------------ */}
            <ul className="flex flex-wrap items-center gap-x-3.5 gap-y-1.5 text-2xs text-ink-2 pt-0.5 font-medium">
              {LEGEND.map((item) => (
                <li key={item.kind} className="flex items-center gap-1.5">
                  <span
                    className={cn('h-2 w-2 rounded-full', DAY_STYLE[item.kind].dotClass)}
                    aria-hidden
                  />
                  {item.displayLabel}
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
